import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import React, { useEffect } from "react";
import { useBrowser } from "src/hybrid/components/Browser";
import createPath from "src/lib/createPath";
import { isHybridApp } from "src/lib/platform";
import translate from "src/lib/translate";
import PromptsCubit from "src/state/PromptsCubit/PromptsCubit";
import SubscriptionCubit from "src/state/SubscriptionCubit/SubscriptionCubit";
import { toast, tracker, useBloc, userState } from "src/state/state";
import { ItemType } from "src/state/TreatmentPlanCubit/TreatmentPlanCubit";
import UserCubit from "src/state/UserCubit/UserCubit";
import UserEmailVerificationCubit from "src/state/UserEmailVerificationCubit/UserEmailVerificationCubit";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import Prompt from "src/ui/components/Prompt/Prompt";
import { useLocation, useNavigate } from "react-router-dom";

export const Prompts: FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [browser] = useBrowser();

  const [
    { allSubscriptions },
    {
      getSubscriptionPurchaseItems,
      hasActiveSubscription,
      hasFinishedSubscription
    }
  ] = useBloc(SubscriptionCubit);
  const [user] = useBloc(UserCubit);
  const [, { initiateVerification }] = useBloc(UserEmailVerificationCubit, {
    subscribe: false,
    create: () => new UserEmailVerificationCubit()
  });
  const [, { lastSignupFunnel }] = useBloc(UserPreferencesCubit);

  const [promptsState, promptsCubit] = useBlocNext(PromptsCubit);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    promptsCubit.evaluate({
      pathname,
      emailVerified: user.userData?.emailVerified ?? false,
      userHasEmail: Boolean(userState.state.userData?.email),
      lastSignupFunnel,
      hasActiveSubscription: hasActiveSubscription ?? false,
      hasFinishedSubscription: hasFinishedSubscription ?? false,
      allSubscriptions: allSubscriptions ?? [],
      getSubscriptionPurchaseItems: (
        subscriptionId: string,
        itemType: ItemType
      ) => getSubscriptionPurchaseItems(subscriptionId, itemType),
      queryString: params.toString()
    });
  }, [
    pathname,
    user.userData?.emailVerified,
    lastSignupFunnel,
    hasActiveSubscription,
    hasFinishedSubscription,
    allSubscriptions,
    promptsCubit,
    getSubscriptionPurchaseItems
  ]);

  const resendVerificationEmailHandle = async (): Promise<void> => {
    await initiateVerification(user.userData?.email);
    toast.show("verify_email_prompt_notification");
  };

  const handleReEnroll = async (): Promise<void> => {
    tracker.track("Reactivate Subscription clicked", {
      data: {
        last_signup_funnel: lastSignupFunnel,
        url: promptsState.reEnrollUrl
      }
    });

    if (isHybridApp()) {
      await browser.open(createPath(promptsState.reEnrollUrl));
    } else {
      navigate(createPath(promptsState.reEnrollUrl));
    }
  };

  return (
    <>
      <Prompt
        title={translate("verify_email_prompt_title")}
        message={user.userData?.email ?? ""}
        action={resendVerificationEmailHandle}
        visible={promptsState.emailVerifyPromptVisible}
        buttonText={translate("verify_email_prompt_button")}
      />

      <Prompt
        title={translate("subscription_inactive_prompt_title")}
        action={handleReEnroll}
        buttonText={translate("reactivateSubscription")}
        visible={promptsState.subscriptionInactivePromptVisible}
      />

      <Prompt
        title={translate("subscription_paused_prompt_title")}
        visible={promptsState.subscriptionPausedPromptVisible}
        textCenter
      />

      <Prompt
        title={translate("subscription_expired_items_prompt_title")}
        visible={promptsState.subscriptionExpiredItemsPromptVisible}
        textCenter
        dangerBackground
      />
    </>
  );
};
